import type { History } from 'history';

export const REPORTING_COMPOSER_FORM_SCROLL_ID = 'reporting-composer-form-scroll';
export const REPORTING_COMPOSER_PREVIEW_SCROLL_ID = 'reporting-composer-preview-scroll';

export type ReportingComposerRestore = {
  formScroll: number,
  previewScroll: number,
  mobileSection: 'compose' | 'preview',
};

export type ReportingComposerLocationState = {
  restore?: ReportingComposerRestore,
};

/** Held across ActivityLogs / other tabs so Back can restore if history state is dropped. */
let pendingReportingComposerRestore: ReportingComposerRestore | null = null;

export const peekPendingReportingComposerRestore = (): ReportingComposerRestore | null => (
  pendingReportingComposerRestore
);

export const consumePendingReportingComposerRestore = (): ReportingComposerRestore | null => {
  const restore = pendingReportingComposerRestore;
  pendingReportingComposerRestore = null;
  return restore;
};

export const clearPendingReportingComposerRestore = () => {
  pendingReportingComposerRestore = null;
};

export const holdReportingComposerRestore = (restore: ReportingComposerRestore) => {
  pendingReportingComposerRestore = restore;
};

const readScrollById = (id: string): number => (
  document.getElementById(id)?.scrollTop ?? 0
);

export const applyReportingComposerScrollById = (id: string, top: number) => {
  const el = document.getElementById(id);
  if (el) el.scrollTop = top;
};

export const snapshotReportingComposerRestore = (
  ui: { mobileSection: 'compose' | 'preview' },
): ReportingComposerRestore => ({
  formScroll: readScrollById(REPORTING_COMPOSER_FORM_SCROLL_ID),
  previewScroll: readScrollById(REPORTING_COMPOSER_PREVIEW_SCROLL_ID),
  mobileSection: ui.mobileSection === 'preview' ? 'preview' : 'compose',
});

export const hasReportingComposerRestoreDepth = (
  restore?: ReportingComposerRestore | null,
): boolean => Boolean(
  restore && (
    restore.formScroll > 0
    || restore.previewScroll > 0
    || restore.mobileSection === 'preview'
  ),
);

/**
 * Write form + preview scrollers onto the current /new URL so browser Back
 * from ActivityLogs (or another tab) reopens the same depths.
 */
export const stashReportingComposerBeforeLeave = (
  history: History,
  ui: { mobileSection: 'compose' | 'preview' },
) => {
  const restore = snapshotReportingComposerRestore(ui);
  pendingReportingComposerRestore = restore;
  const prev = (history.location.state || {}) as ReportingComposerLocationState;
  history.replace({
    pathname: history.location.pathname,
    search: history.location.search,
    state: {
      ...prev,
      restore,
    } as ReportingComposerLocationState,
  });
};
